import { createEffect, createEvent, createStore, sample, split } from 'effector';
import { api, unwrap, type Schemas } from '../../shared/api/client';
import { i18n } from '../../shared/lib/i18n';
import { isLocale, type Locale } from '../../shared/lib/locale';
import { readJson, writeJson } from '../../shared/lib/storage';

export type ThemePreference = 'system' | 'dark' | 'light';
export const THEMES: readonly ThemePreference[] = ['system', 'dark', 'light'];

const STORAGE_KEY = 'botimir:preferences';

interface LocalPreferences {
  theme?: ThemePreference;
  locale?: Locale;
  sidebar?: boolean;
  inspector?: boolean;
}

const stored = readJson<LocalPreferences>(STORAGE_KEY, {});

function persist(patch: LocalPreferences): void {
  writeJson(STORAGE_KEY, { ...readJson<LocalPreferences>(STORAGE_KEY, {}), ...patch });
}

// --- Theme (per device) ----------------------------------------------------------------------

export const themeChanged = createEvent<ThemePreference>();
export const systemThemeChanged = createEvent();

export const $theme = createStore<ThemePreference>(
  stored.theme && THEMES.includes(stored.theme) ? stored.theme : 'system',
).on(themeChanged, (_, theme) => theme);

export function resolveTheme(theme: ThemePreference): 'dark' | 'light' {
  if (theme !== 'system') return theme;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

const applyThemeFx = createEffect((theme: ThemePreference) => {
  document.documentElement.dataset.theme = resolveTheme(theme);
  persist({ theme });
});

sample({ clock: [themeChanged, systemThemeChanged], source: $theme, target: applyThemeFx });

// --- Panels (per device) -----------------------------------------------------------------------

export const sidebarToggled = createEvent();
export const inspectorToggled = createEvent();
export const inspectorOpened = createEvent();

export const $sidebarOpen = createStore(stored.sidebar ?? true).on(sidebarToggled, (open) => !open);
export const $inspectorOpen = createStore(stored.inspector ?? true)
  .on(inspectorToggled, (open) => !open)
  .on(inspectorOpened, () => true);

$sidebarOpen.updates.watch((sidebar) => {
  persist({ sidebar });
});
$inspectorOpen.updates.watch((inspector) => {
  persist({ inspector });
});

// --- Server preferences (owner-wide: defaults, notifications, language) ----------------------

export const loadPreferencesFx = createEffect(() =>
  unwrap(api.GET('/api/v1/settings/preferences')),
);

export const savePreferencesFx = createEffect((prefs: Schemas['Preferences-Input']) =>
  unwrap(api.PUT('/api/v1/settings/preferences', { body: prefs })),
);

export type Preferences = Schemas['Preferences-Output'];

/** Change some preferences: shown at once, then saved. */
export const preferencesPatched = createEvent<Partial<Preferences>>();
/** Every change so far is saved. */
export const preferencesSaved = createEvent<Preferences>();
/** A save failed; the preferences show what the server has again. */
export const preferencesSaveFailed = createEvent<Error>();

export const $preferences = createStore<Preferences | null>(null)
  .on(loadPreferencesFx.doneData, (_, prefs) => prefs)
  .on(preferencesPatched, (prefs, patch) => (prefs ? { ...prefs, ...patch } : prefs))
  .on(preferencesSaved, (_, prefs) => prefs);

// Saves run one at a time and always send everything changed so far, so quick successive changes
// can't reach the server out of order or undo each other.
/** Save what the preferences hold now. */
const saveWanted = createEvent();
sample({
  clock: saveWanted,
  source: $preferences,
  filter: (prefs): prefs is Preferences => prefs !== null,
  target: savePreferencesFx,
});
/** A change arrived while a save was running: save again when it ends. */
const $queued = createStore(false);

sample({
  clock: preferencesPatched,
  source: { prefs: $preferences, saving: savePreferencesFx.pending },
  filter: ({ prefs, saving }) => prefs !== null && !saving,
  fn: () => undefined,
  target: saveWanted,
});
sample({
  clock: preferencesPatched,
  source: savePreferencesFx.pending,
  filter: Boolean,
  fn: () => true,
  target: $queued,
});

const saveEnded = sample({
  clock: savePreferencesFx.finally,
  source: $queued,
  fn: (queued, result) => ({ queued, result }),
});
const saveAgain = sample({ clock: saveEnded, filter: ({ queued }) => queued });
$queued.reset(saveAgain);
sample({ clock: saveAgain, target: saveWanted });
// Settled: no change is waiting, so the server has everything (or the save failed).
interface SaveDone {
  status: 'done';
  result: Preferences;
}
interface SaveFailed {
  status: 'fail';
  error: Error;
}
const settled = sample({
  clock: saveEnded,
  filter: ({ queued }) => !queued,
  fn: ({ result }): SaveDone | SaveFailed =>
    result.status === 'done'
      ? { status: 'done', result: result.result }
      : { status: 'fail', error: result.error },
});
const ended = split(settled, {
  ok: (r): r is SaveDone => r.status === 'done',
  failed: (r): r is SaveFailed => r.status === 'fail',
});
sample({ clock: ended.ok, fn: (r) => r.result, target: preferencesSaved });
sample({ clock: ended.failed, fn: (r) => r.error, target: preferencesSaveFailed });
sample({ clock: preferencesSaveFailed, target: loadPreferencesFx });

// --- Language ------------------------------------------------------------------------------------

export const localeChanged = createEvent<Locale>();

/** A saved choice wins; otherwise the browser's language (Bulgarian or English). */
function initialLocale(): Locale {
  if (isLocale(stored.locale)) return stored.locale;
  return /^bg\b/i.test(navigator.language) ? 'bg' : 'en';
}

export const $locale = createStore<Locale>(initialLocale())
  .on(localeChanged, (_, locale) => locale)
  .on($preferences.updates, (current, prefs) =>
    prefs && isLocale(prefs.locale) ? prefs.locale : current,
  );

const applyLocaleFx = createEffect((locale: Locale) => {
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
  persist({ locale });
});

sample({ clock: $locale.updates, target: applyLocaleFx });

// Changing the language saves it with the owner's preferences (emails use it too).
sample({
  clock: localeChanged,
  source: $preferences,
  filter: (prefs, locale) => prefs !== null && prefs.locale !== locale,
  fn: (_, locale) => ({ locale }),
  target: preferencesPatched,
});
